import React, { useMemo } from 'react';
import { translate } from 'components/Translation';
import Card from 'components/Card/Card';
import styles from 'assets/jss/material-dashboard-pro-react/components/numberSettingsCardStyle';
import { makeStyles } from '@material-ui/core';
import classNames from 'classnames';
import { BodyS, HeadingS } from 'components/Typography/Typography';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import CustomTooltip from '../CustomTooltip/CustomTooltip';

// @ts-ignore
const useStyles = makeStyles(styles);

interface INumberSettingsCardProps {
	disabled?: boolean;
	image: React.ReactElement | string;
	text: string;
	notAvailable?: boolean;
	onClick?: () => void;
	error?: boolean;
	tooltip?: string;
	simManagementUnavailable?: boolean;
}

const NumberSettingsCard = ({
	image,
	disabled,
	text,
	notAvailable,
	error,
	tooltip,
	simManagementUnavailable,
	...rest
}: INumberSettingsCardProps) => {
	const classes = useStyles();

	const card = classNames({
		[classes.card]: true,
		[classes.disabled]: disabled || simManagementUnavailable,
		[classes.notAvailable]: notAvailable,
		[classes.error]: error,
	});
	const imageClass = classNames({
		[classes.imageWrapper]: true,
		[classes.imageDisabled]: simManagementUnavailable,
	});

	const icon = typeof image === 'string' ? <img src={image} alt={text} /> : image;

	const textColor = useMemo(() => {
		if (disabled || simManagementUnavailable) {
			return 'disabled-light';
		}
		if (error) {
			return 'danger';
		}
		return 'brand';
	}, [disabled, simManagementUnavailable, error]);

	return (
		<Card className={card} {...rest}>
			<div className={imageClass}>{icon}</div>
			<div className={classes.textWrapper}>
				<ConditionalRender
					show={!!disabled}
					onTrue={() => (
						<div className={classes.notAvailableText}>
							<BodyS color="disabled-light" disableMargin>
								{translate('numbers.settings.will.be.available')}
							</BodyS>
						</div>
					)}
				/>
				<HeadingS color={textColor} disableMargin>
					{text}
				</HeadingS>
				<ConditionalRender
					show={!!tooltip}
					onTrue={() => (
						<div className={classes.tooltipWrapper}>
							<CustomTooltip placement="top" size="sm">
								{tooltip || ''}
							</CustomTooltip>
						</div>
					)}
				/>
			</div>
		</Card>
	);
};

export default NumberSettingsCard;
